import React, { useState, useCallback } from 'react';
import {
  View,
  StyleSheet,
  Modal,
  Pressable,
  Text,
  Platform,
  useWindowDimensions,
  StatusBar,
} from 'react-native';
import {
  Gesture,
  GestureDetector,
  GestureHandlerRootView,
} from 'react-native-gesture-handler';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSpring,
  runOnJS,
} from 'react-native-reanimated';
import { Feather } from '@expo/vector-icons';
import { Image } from 'expo-image';

interface ImageViewerProps {
  images: string[];
  initialIndex: number;
  visible: boolean;
  onClose: () => void;
}

export default function ImageViewer({ images, initialIndex, visible, onClose }: ImageViewerProps) {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Animation values
  const scale = useSharedValue(1);
  const savedScale = useSharedValue(1);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const savedTranslateX = useSharedValue(0);
  const savedTranslateY = useSharedValue(0);
  const backdropOpacity = useSharedValue(1);

  // Reset when modal opens
  React.useEffect(() => {
    if (visible) {
      setCurrentIndex(initialIndex);
      scale.value = 1;
      savedScale.value = 1;
      translateX.value = 0;
      translateY.value = 0;
      savedTranslateX.value = 0;
      savedTranslateY.value = 0;
      backdropOpacity.value = 1;
    }
  }, [visible, initialIndex]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      // Reset zoom when changing images
      scale.value = withTiming(1);
      savedScale.value = 1;
      translateX.value = withTiming(0);
      translateY.value = withTiming(0);
      savedTranslateX.value = 0;
      savedTranslateY.value = 0;
    }
  }, [currentIndex]);

  const goToNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
      // Reset zoom when changing images
      scale.value = withTiming(1);
      savedScale.value = 1;
      translateX.value = withTiming(0);
      translateY.value = withTiming(0);
      savedTranslateX.value = 0;
      savedTranslateY.value = 0;
    }
  }, [currentIndex, images.length]);

  // Track if a gesture is active to prevent accidental closes
  const isGestureActive = useSharedValue(false);

  // Pinch gesture for zoom
  const pinchGesture = Gesture.Pinch()
    .onStart(() => {
      isGestureActive.value = true;
    })
    .onUpdate((e) => {
      scale.value = savedScale.value * e.scale;
    })
    .onEnd(() => {
      isGestureActive.value = false;
      if (scale.value < 1) {
        scale.value = withSpring(1);
        savedScale.value = 1;
        translateX.value = withSpring(0);
        translateY.value = withSpring(0);
        savedTranslateX.value = 0;
        savedTranslateY.value = 0;
      } else if (scale.value > 4) {
        scale.value = withSpring(4);
        savedScale.value = 4;
      } else {
        savedScale.value = scale.value;
      }
    });

  // Pan gesture for dragging
  const panGesture = Gesture.Pan()
    .minDistance(10) // Require minimum movement to activate
    .onStart(() => {
      isGestureActive.value = true;
    })
    .onUpdate((e) => {
      if (savedScale.value > 1) {
        // When zoomed, allow panning the image
        translateX.value = savedTranslateX.value + e.translationX;
        translateY.value = savedTranslateY.value + e.translationY;
      } else {
        // When not zoomed, check if horizontal or vertical swipe
        if (Math.abs(e.translationX) > Math.abs(e.translationY)) {
          // Horizontal swipe - for changing images
          translateX.value = e.translationX;
        } else {
          // Vertical drag to dismiss
          translateY.value = e.translationY;
          backdropOpacity.value = Math.max(0, 1 - Math.abs(e.translationY) / 300);
        }
      }
    })
    .onEnd((e) => {
      isGestureActive.value = false;
      if (savedScale.value > 1) {
        // Save pan position when zoomed
        savedTranslateX.value = translateX.value;
        savedTranslateY.value = translateY.value;
      } else {
        // Check for horizontal swipe to change image
        if (Math.abs(e.translationX) > Math.abs(e.translationY) && Math.abs(e.translationX) > 50) {
          if (e.translationX > 0 && currentIndex > 0) {
            runOnJS(goToPrevious)();
          } else if (e.translationX < 0 && currentIndex < images.length - 1) {
            runOnJS(goToNext)();
          }
          translateX.value = withSpring(0);
        } else if (Math.abs(e.translationY) > 100) {
          // Dismiss if dragged down/up enough
          backdropOpacity.value = withTiming(0);
          translateY.value = withTiming(e.translationY > 0 ? screenHeight : -screenHeight, {}, () => {
            runOnJS(handleClose)();
          });
        } else {
          // Snap back
          translateX.value = withSpring(0);
          translateY.value = withSpring(0);
          backdropOpacity.value = withTiming(1);
        }
      }
    });

  // Double tap to zoom
  const doubleTapGesture = Gesture.Tap()
    .numberOfTaps(2)
    .maxDuration(300)
    .onEnd(() => {
      if (scale.value > 1) {
        // Zoom out
        scale.value = withSpring(1);
        savedScale.value = 1;
        translateX.value = withSpring(0);
        translateY.value = withSpring(0);
        savedTranslateX.value = 0;
        savedTranslateY.value = 0;
      } else {
        // Zoom in to 2x
        scale.value = withSpring(2);
        savedScale.value = 2;
      }
    });

  // Compose gestures - pinch and pan run simultaneously, double tap is separate
  const composedGestures = Gesture.Race(
    doubleTapGesture,
    Gesture.Simultaneous(pinchGesture, panGesture)
  );

  const imageAnimatedStyle = useAnimatedStyle(() => {
    // When not zoomed and swiping horizontally, add some resistance
    const tx = savedScale.value > 1 ? translateX.value : translateX.value * 0.3;
    return {
      transform: [
        { translateX: tx },
        { translateY: translateY.value },
        { scale: scale.value },
      ],
    };
  });

  const backdropAnimatedStyle = useAnimatedStyle(() => ({
    opacity: backdropOpacity.value,
  }));

  if (Platform.OS === 'web') {
    // Web lightbox style
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
        <View style={styles.webContainer}>
          <Pressable style={styles.webBackdrop} onPress={handleClose} />

          {/* Close button */}
          <Pressable style={styles.webCloseButton} onPress={handleClose}>
            <Feather name="x" size={28} color="#fff" />
          </Pressable>

          {/* Image counter */}
          {images.length > 1 && (
            <View style={styles.webCounter}>
              <Text style={styles.webCounterText}>
                {currentIndex + 1} / {images.length}
              </Text>
            </View>
          )}

          {/* Previous button */}
          {currentIndex > 0 && (
            <Pressable style={[styles.webNavButton, styles.webNavLeft]} onPress={goToPrevious}>
              <View style={styles.webNavButtonInner}>
                <Feather name="chevron-left" size={32} color="#fff" />
              </View>
            </Pressable>
          )}

          {/* Image */}
          <View style={styles.webImageContainer}>
            <Image
              source={{ uri: images[currentIndex] }}
              style={styles.webImage}
              contentFit="contain"
            />
          </View>

          {/* Next button */}
          {currentIndex < images.length - 1 && (
            <Pressable style={[styles.webNavButton, styles.webNavRight]} onPress={goToNext}>
              <View style={styles.webNavButtonInner}>
                <Feather name="chevron-right" size={32} color="#fff" />
              </View>
            </Pressable>
          )}
        </View>
      </Modal>
    );
  }

  // Mobile fullscreen viewer with gestures
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <StatusBar hidden />
      <GestureHandlerRootView style={styles.container}>
        <Animated.View style={[styles.backdrop, backdropAnimatedStyle]} />

        {/* Close button */}
        <Pressable style={styles.closeButton} onPress={handleClose}>
          <View style={styles.closeButtonInner}>
            <Feather name="x" size={24} color="#fff" />
          </View>
        </Pressable>

        {/* Image counter */}
        {images.length > 1 && (
          <View style={styles.counter}>
            <Text style={styles.counterText}>
              {currentIndex + 1} / {images.length}
            </Text>
          </View>
        )}

        {/* Swipe hint for multiple images */}
        {images.length > 1 && (
          <View style={styles.swipeHint}>
            <Text style={styles.swipeHintText}>Swipe to see more</Text>
          </View>
        )}

        <GestureDetector gesture={composedGestures}>
          <Animated.View style={[styles.imageWrapper, imageAnimatedStyle]}>
            <Image
              source={{ uri: images[currentIndex] }}
              style={[styles.image, { width: screenWidth, height: screenHeight }]}
              contentFit="contain"
            />
          </Animated.View>
        </GestureDetector>

        {/* Indicators */}
        {images.length > 1 && (
          <View style={styles.indicators}>
            {images.map((_, index) => (
              <View
                key={index}
                style={[
                  styles.indicator,
                  { backgroundColor: index === currentIndex ? '#fff' : 'rgba(255,255,255,0.4)' },
                ]}
              />
            ))}
          </View>
        )}
      </GestureHandlerRootView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
  },
  closeButton: {
    position: 'absolute',
    top: 50,
    right: 20,
    zIndex: 10,
  },
  closeButtonInner: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  counter: {
    position: 'absolute',
    top: 50,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 10,
  },
  counterText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  swipeHint: {
    position: 'absolute',
    top: 80,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 10,
  },
  swipeHintText: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 12,
  },
  imageWrapper: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    flex: 1,
  },
  indicators: {
    position: 'absolute',
    bottom: 50,
    flexDirection: 'row',
    gap: 8,
  },
  indicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  // Web styles
  webContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  webBackdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  webCloseButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    zIndex: 10,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  webCounter: {
    position: 'absolute',
    top: 20,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 10,
  },
  webCounterText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '500',
  },
  webImageContainer: {
    maxWidth: '90%',
    maxHeight: '85%',
    width: '100%',
    height: '100%',
  },
  webImage: {
    width: '100%',
    height: '100%',
  },
  webNavButton: {
    position: 'absolute',
    top: '50%',
    marginTop: -28,
    zIndex: 10,
  },
  webNavLeft: {
    left: 20,
  },
  webNavRight: {
    right: 20,
  },
  webNavButtonInner: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
});
